import { Dropdown, Tag } from '@carbon/react';
import {
  LifecycleStatusTag,
  NameWithStatusTag,
  TypeCurveStatusTag,
} from 'components/lifecycle-status-tag';
import { Locked, WarningAltFilled } from '@carbon/icons-react';
import classNames from 'classnames';
import type { DataTableColumnDef } from 'components/data-table/types';
import { FloatingTooltip } from 'components/floating-tooltip';
import { LaunchLink } from 'components/launch-link';
import { formatNumber } from 'utils/number';
import type {
  InventoryFormationCandidate,
  InventoryFormationCandidateRow,
} from 'types/api-responses';
import { colorForArea } from './area-colors';
import style from './inventory-context-columns.module.scss';

/**
 * A package the Inventory dropdown offers, from either source the modal draws
 * on: the workspace's own zones (which carry `inventoryFormationPackageId`, so a
 * pick can re-key the selected leaf) or, for a zone the workspace doesn't own
 * yet, a launch candidate (no id).
 */
export interface ContextPackageOption {
  /** The zone id, or null for a launch candidate the workspace doesn't own. */
  inventoryFormationPackageId: number | null;
  name: string | null;
  wellSpacingFt: number | null;
  displayName: string | null;
  isDefault: boolean;
  ltdProperties?: number | null;
  /** How many of `ltdProperties` are valued — package-anchor responses only. */
  valuedLTDProperties?: number;
  /** The selected run's valued count per curve it priced these places on (see the API type). */
  calculationValued?: { uniqueTcId: string; valuedLTDProperties: number }[];
  /**
   * The slot rows behind `ltdProperties`, and their extent — package-anchor responses only (see
   * `InventoryCandidatePackage`). Carried on the option, not the row, because the map follows the
   * Inventory dropdown: picking another package must redraw and reframe without a round trip.
   */
  ltdSlotIds?: number[];
  bbox?: [number, number, number, number];
}

/**
 * A stable identity for a package option across a candidates refetch. Keyed on
 * the zone id when the option is a workspace zone — the same package name recurs
 * at different spacings, so the id keeps them distinct — else on the display
 * name for a launch candidate that carries no id.
 *
 * @param {ContextPackageOption} option - The package option.
 * @return {string} A key stable for the life of the modal.
 */
export const packageOptionKey = (option: ContextPackageOption): string => {
  return option.inventoryFormationPackageId !== null
    ? `id:${option.inventoryFormationPackageId}`
    : `name:${option.displayName ?? ''}`;
};

/**
 * An (area, formation) pair with everything its cells need already resolved.
 *
 * The derived fields matter for more than convenience: anything the cells read
 * has to arrive through the table's `data`, never through the column defs. A new
 * `columns` array makes `DataTable` rebuild its rows, which detaches the DOM —
 * closing any open Carbon menu and dropping the interaction. Keeping the column
 * defs stable and moving the volatile parts here avoids that entirely.
 */
export interface FormationRow extends InventoryFormationCandidateRow {
  /** DataTable reads this to disable the row's checkbox (data-table.tsx). */
  _canSelect: boolean;
  /** The chosen type curve — the row's default until the operator picks. */
  _defaultTypeCurve: InventoryFormationCandidate | null;
  /** The chosen inventory package — the zone's default until picked. */
  _defaultPackage: ContextPackageOption | null;
  /** True while the row is on its default type curve (not operator-overridden). */
  _isDefaultTypeCurve: boolean;
  /** True while the row is on its default inventory package. */
  _isDefaultPackage: boolean;
  /**
   * The zone's packages, already resolved by the row builder — including the
   * bridge that reads them off the default type curve on responses that still
   * nest them there. The cell must not re-derive this: `inventoryPackages` is
   * absent on those responses.
   */
  _packages: ContextPackageOption[];
  /** Palette slot for the area — shared with the preview map. */
  _areaColorIndex: number;
  /** Cross-highlighted with the preview map. */
  _isActive: boolean;
  /** DataTable constrains TData to an index-signature record. */
  [key: string]: unknown;
}

/**
 * The launched-pair row — the one carrying the origin type curve.
 *
 * Origin is marked at the TYPE-CURVE level (exactly one across all rows, which
 * the BE guarantees), not at the row level: a row's own `isOrigin` flags the
 * whole origin area, so several rows carry it. Only the row that actually holds
 * the launched type curve is locked into the workspace.
 *
 * @param {object} row - A candidate row (or derived FormationRow).
 * @return {boolean} True when one of the row's type curves is the origin.
 */
export const rowHasOriginTypeCurve = (row: {
  typeCurves: Pick<InventoryFormationCandidate, 'isOrigin'>[];
}): boolean => row.typeCurves.some((tc) => tc.isOrigin);

/**
 * The spacings behind a row's current picks, when they disagree.
 *
 * Pairing a curve with a package laid out at a different spacing is legal (the curve
 * prices whatever slots the package provides) but worth flagging. Unknown spacing on
 * either side means no warning.
 *
 * @param {object} row - The row's chosen halves (draft-aware picks).
 * @return {object|null} Both spacings ({curveFt, packageFt}) when they differ; null
 *   when either is unknown or they agree.
 */
export const spacingMismatch = (row: {
  _defaultTypeCurve: InventoryFormationCandidate | null;
  _defaultPackage: ContextPackageOption | null;
}): { curveFt: number; packageFt: number } | null => {
  const curveFt = row._defaultTypeCurve?.wellSpacingFt ?? null;
  const packageFt = row._defaultPackage?.wellSpacingFt ?? null;
  if (curveFt === null || packageFt === null || curveFt === packageFt) {
    return null;
  }
  return { curveFt, packageFt };
};

/**
 * How many of a row's LTD properties are valued, for the "(n)" in LTD Properties.
 *
 * The selected run's verdict when the row is still on a curve that run priced this package on,
 * so places the run dropped at runtime do not count. Otherwise the live count: the inventory's
 * verdict, which is also what the page shows once a pick the run never priced is applied. A row
 * with a package picked reads only that package's figures; the row's own count is its default
 * package's, so it stands in only when no package is picked.
 *
 * @param {object} row - The row's chosen curve and package, and its own live count.
 * @return {number|undefined} The valued count, or undefined when the response carries none.
 */
export const valuedLtdCount = (row: {
  _defaultTypeCurve: InventoryFormationCandidate | null;
  _defaultPackage: ContextPackageOption | null;
  valuedLTDProperties?: number;
}): number | undefined => {
  const pkg = row._defaultPackage;
  if (!pkg) {
    return row.valuedLTDProperties;
  }
  const curve = row._defaultTypeCurve?.uniqueTcId;
  const fromRun = pkg.calculationValued?.find(
    (entry) => entry.uniqueTcId === curve
  );
  return fromRun?.valuedLTDProperties ?? pkg.valuedLTDProperties;
};

// Leftmost so a flagged row reads as flagged before anything else. Always present, empty on clean
// rows: toggling it would change `columns`, which remounts every cell. FloatingTooltip, not a
// Carbon tooltip: Carbon's popover renders inside the overflow-hidden cell and is clipped,
// while FloatingTooltip portals to <body>.
// The warning glyph, and the column that holds it: the cell pads 16px a side, so 64px leaves room
// for the 24px icon with a little to spare.
const WARNING_ICON_SIZE = 24;
const WARNING_COLUMN_SIZE = 64;

const rowWarningsColumn: DataTableColumnDef<FormationRow> = {
  id: 'rowWarnings',
  header: '',
  size: WARNING_COLUMN_SIZE,
  minSize: WARNING_COLUMN_SIZE,
  maxSize: WARNING_COLUMN_SIZE,
  enableSorting: false,
  enableResizing: false,
  // The modal enables the column-visibility menu. toggleColumnFilter keeps this unlabelled column
  // out of it, so "Hide all" and the per-column toggles never reach it; enableHiding is a backup
  // for any other caller that hides columns through TanStack.
  enableHiding: false,
  // fixedWidth: otherwise first paint widens this column with the spare width like any other.
  meta: { toggleColumnFilter: false, fixedWidth: true },
  cell: ({ row }) => {
    const mismatch = spacingMismatch(row.original);
    if (!mismatch) {
      return null;
    }
    return (
      <div className={style.rowWarningCell}>
        <FloatingTooltip
          placement="right"
          content={
            <p>
              The in-use type curve assumes{' '}
              {formatNumber({ value: mismatch.curveFt, minPrecision: 0 })} ft
              spacing, but the inventory source lays the slots out at{' '}
              {formatNumber({ value: mismatch.packageFt, minPrecision: 0 })} ft.
              You can keep this combination — the curve prices whatever slots
              the package provides.
            </p>
          }
        >
          <WarningAltFilled
            aria-label="Spacing mismatch"
            size={WARNING_ICON_SIZE}
            className={style.rowWarningIcon}
          />
        </FloatingTooltip>
      </div>
    );
  },
};

interface BuildArgs {
  onActivate?: (rowId: string) => void;
  /** Row picked another Type Curve. */
  onPickTypeCurve?: (rowId: string, uniqueTcId: string) => void;
  /** Row picked another Inventory package, keyed by `packageOptionKey`. */
  onPickPackage?: (rowId: string, packageKey: string) => void;
  /**
   * Render the area name as a link to the row's type curve instead of as the row activator
   * (default false). The eval's TC Manager opts in; the launch and Add-or-Remove modals keep the
   * button, since changing an affordance in someone else's flow is not this change's business.
   */
  linkAreaToTypeCurve?: boolean;
  /**
   * Show how many of each row's LTD properties are valued, as "4 (1)" under "LTD Properties
   * (valued)" (default false). Eval only: the count comes from the package-anchor response.
   */
  showValuedLtd?: boolean;
}

/**
 * Columns for the launch picker's inventory context table.
 *
 * One row per (area, formation) pair, matching both
 * `POST /queries/inventory_formation_candidates` and the grain the create
 * payload enumerates. Area and formation are row identity rather than editable
 * cells — the user chooses which pairs to include, via the checkboxes.
 *
 * Type Curve and Inventory are read-only: the create payload carries neither,
 * and the server resolves both from the pair. They show what it will pick.
 *
 * Clicking a row activates it, which highlights it here and fills its area on
 * the preview map. The handler sits on the cells rather than the row because
 * `DataTable` discards row clicks whenever `selectionType` is `multi`
 * (data-table.tsx) — the checkboxes own selection there.
 *
 * @param {BuildArgs} args - Row activation handler.
 * @return {DataTableColumnDef<FormationRow>[]} Column definitions.
 */
export const buildInventoryContextColumns = ({
  onActivate,
  linkAreaToTypeCurve = false,
  onPickTypeCurve,
  onPickPackage,
  showValuedLtd = false,
}: BuildArgs): DataTableColumnDef<FormationRow>[] => [
  rowWarningsColumn,
  {
    id: 'typeCurveArea',
    header: 'Type Curve Area',
    accessorKey: 'typeCurveArea',
    cell: ({ row }) => {
      const {
        id,
        typeCurveArea,
        isOrigin,
        overlapPercent,
        _areaColorIndex,
        _isActive,
        _defaultTypeCurve,
      } = row.original;

      // The area name is a real link now, to the row's CHOSEN type curve. The area itself has no
      // page — `/type_curve_areas/:id` is a JSON-only endpoint that 406s in a browser — and the
      // curve is what a reader wants from this row anyway, which is the same substitution the
      // eval's slot cells make.
      //
      // A NEW TAB is not a preference: this modal holds uncommitted picks, and a same-tab
      // navigation would throw them away. Row activation is unaffected — the Formation and
      // LTD Properties cells are full-cell activators, so the row still selects on click.
      // The server's href (minted from `tc_public_id`); null when the curve has no page.
      const curveHref = linkAreaToTypeCurve
        ? (_defaultTypeCurve?.href ?? null)
        : null;

      // The mock's areaBlock: a colour rule keyed to the same palette slot the
      // map uses, then the area name. Sharing `colorForArea` is the point — the
      // chip and the polygon have to read as the same area. Trailing the name:
      // an Origin tag for the launched-from area, else its overlap %.
      return (
        <span className={style.areaBlock}>
          <span
            className={style.areaChip}
            style={{ background: colorForArea(_areaColorIndex) }}
            aria-hidden="true"
          />
          {curveHref ? (
            <a
              className={style.areaName}
              href={curveHref}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${
                _defaultTypeCurve?.label ?? typeCurveArea
              } in a new tab`}
            >
              {typeCurveArea}
            </a>
          ) : (
            // No curve resolved for the pair (so no page to open): the name stays the activator
            // it has always been rather than rendering a dead link.
            <button
              type="button"
              className={style.areaName}
              aria-pressed={_isActive}
              onClick={() => onActivate?.(id)}
            >
              {typeCurveArea}
            </button>
          )}
          {isOrigin ? (
            <Tag type="teal" size="sm">
              Origin
            </Tag>
          ) : overlapPercent !== null && overlapPercent !== undefined ? (
            <span className={style.overlapText}>{overlapPercent}% overlap</span>
          ) : null}
        </span>
      );
    },
  },
  {
    id: 'formation',
    header: 'Formation',
    accessorKey: 'formation',
    size: 120,
    cell: ({ row }) => {
      const { id, formation, interval, color, _isActive } = row.original;
      return (
        <button
          type="button"
          className={style.rowActivator}
          aria-pressed={_isActive}
          onClick={() => onActivate?.(id)}
        >
          {/* Formation colour comes straight from the BE row (`color` hex). */}
          {color && (
            <span
              className={style.formationSwatch}
              style={{ background: color }}
              aria-hidden="true"
            />
          )}
          <span className={style.formationName}>{formation}</span>
          {interval && (
            <span className={style.formationInterval}>{interval}</span>
          )}
          {rowHasOriginTypeCurve(row.original) && (
            <>
              <Locked
                size={16}
                className={style.lockIcon}
                aria-label="Launched pair — always included"
              />
              {/* The mock marks the launched row here, not on the Type Curve
                  column, and calls it Origin. */}
              <Tag type="teal" size="sm">
                Origin
              </Tag>
            </>
          )}
        </button>
      );
    },
  },
  {
    id: 'typeCurve',
    header: 'Type Curve',
    // The value is a full unique_tc_id inside a dropdown, so it needs room for
    // the text plus the chevron — "Midland_MDG_proj92_A_WCA_1320_20220718" is
    // 38 characters and was truncating at 280.
    size: 380,
    // The sm dropdown is 32px in a 23.4px content box: the default truncating cell clips its
    // top and bottom, which shows as a missing top edge once it is focused.
    meta: { overflowVisible: true },
    accessorFn: (row) => row._defaultTypeCurve?.label ?? '',
    // Every row renders the dropdown, so the column stays on one alignment. The
    // origin row's is disabled rather than swapped for a label: its curve is
    // the one the workspace launches from and cannot change, and the lock and
    // Origin tag in the Formation cell already say so.
    cell: ({ row }) => {
      const { id, typeCurves, _defaultTypeCurve, _canSelect } = row.original;
      if (!_defaultTypeCurve) {
        return <span className={style.emptyCell}>—</span>;
      }

      return (
        <span className={style.tcCell}>
          <Dropdown
            id={`ctx-curve-${id}`}
            className={style.cellDropdown}
            size="sm"
            // Floats the menu: in flow it grows its row, and the table's own
            // scroll container clips it at the edge.
            autoAlign
            // Visually hidden but still the control's accessible name — an empty
            // titleText leaves the combobox unnamed for assistive tech.
            titleText="Type Curve"
            hideLabel
            label="Select a Type Curve"
            disabled={!_canSelect}
            items={typeCurves}
            selectedItem={_defaultTypeCurve}
            itemToString={(item: InventoryFormationCandidate | null) => {
              return item ? item.label : '';
            }}
            itemToElement={(item: InventoryFormationCandidate) => (
              <NameWithStatusTag
                tag={
                  <TypeCurveStatusTag
                    promoted={item.promoted}
                    isDefault={item.isDefault}
                    archived={item.archived}
                  />
                }
              >
                {item.label}
              </NameWithStatusTag>
            )}
            onChange={({
              selectedItem,
            }: {
              selectedItem?: InventoryFormationCandidate | null;
            }) => {
              if (selectedItem) onPickTypeCurve?.(id, selectedItem.uniqueTcId);
            }}
          />
          {/* The lifecycle tag reads the curve's own zone-default flag, as the tree and the
              listing do. _isDefaultTypeCurve means "still on the pick this modal opened with",
              a different question, so it does not drive the Default tag. */}
          <TypeCurveStatusTag
            promoted={_defaultTypeCurve.promoted}
            isDefault={_defaultTypeCurve.isDefault}
            archived={_defaultTypeCurve.archived}
          />
          {/* Origin answers a different question (which curve the workspace was launched
              from), so it sits beside the lifecycle tag rather than replacing it. */}
          {_defaultTypeCurve.isOrigin ? (
            <Tag type="teal" size="sm">
              Origin
            </Tag>
          ) : null}
          {/* Open the selected type curve in a new tab. Locked (origin) rows
              can't change their curve, so they show no launch affordance. */}
          {_canSelect && _defaultTypeCurve.href && (
            <LaunchLink
              className={style.launchButton}
              to={_defaultTypeCurve.href}
              label={`Open ${_defaultTypeCurve.label} in a new tab`}
            />
          )}
        </span>
      );
    },
  },
  {
    id: 'inventory',
    header: 'Inventory',
    size: 280,
    // The sm dropdown is 32px in a 23.4px content box: the default truncating cell clips its
    // top and bottom, which shows as a missing top edge once it is focused.
    meta: { overflowVisible: true },
    // `displayName` carries the spacing ("Base -- 1320"), which is what makes a
    // package identifiable: the same name recurs at different spacings.
    accessorFn: (row) => row._defaultPackage?.displayName ?? '',
    cell: ({ row }) => {
      const { id, _packages, _defaultPackage, _isDefaultPackage } =
        row.original;

      // An empty list is a real state, not missing data: nothing is promoted
      // for the zone yet. It still renders a dropdown — disabled, reading "No
      // inventory yet" — so the column keeps one alignment down the table.
      const empty = _packages.length === 0;

      // Inventory is always selectable, even on Origin rows (whose Type Curve is
      // locked): only a zone with no promoted packages stays disabled.
      return (
        <span className={style.inventoryCell}>
          <Dropdown
            id={`ctx-inventory-${id}`}
            className={style.cellDropdown}
            size="sm"
            autoAlign
            titleText="Inventory"
            hideLabel
            label={empty ? 'No inventory yet' : 'Select an inventory'}
            disabled={empty}
            items={_packages}
            selectedItem={_defaultPackage}
            itemToString={(item: ContextPackageOption | null) => {
              return item ? item.displayName || item.name || '' : '';
            }}
            onChange={({
              selectedItem,
            }: {
              selectedItem?: ContextPackageOption | null;
            }) => {
              if (selectedItem) {
                onPickPackage?.(id, packageOptionKey(selectedItem));
              }
            }}
          />
          {/* Always rendered, hidden off the default, so every row reserves the tag's room
              and the dropdown keeps one width down the column. */}
          <LifecycleStatusTag
            status="default"
            className={classNames({
              [style.hiddenTag]: empty || !_isDefaultPackage,
            })}
            aria-hidden={empty || !_isDefaultPackage || undefined}
          />
        </span>
      );
    },
  },
  {
    id: 'locations',
    header: showValuedLtd ? 'LTD Properties (valued)' : 'LTD Properties',
    // Follows the Inventory dropdown: each package carries its own count, so
    // switching re-reads it here with no follow-up request. The row-level value
    // is what the default package would inherit.
    accessorFn: (row) =>
      row._defaultPackage?.ltdProperties ?? row.ltdProperties ?? null,
    cell: ({ row }) => {
      const { id, _defaultPackage, _isActive } = row.original;
      const slotCount =
        _defaultPackage?.ltdProperties ?? row.original.ltdProperties;
      const valuedCount = showValuedLtd
        ? valuedLtdCount(row.original)
        : undefined;

      // Also an activation target: it owns no control, so it widens the area
      // of the row that responds to a click.
      return (
        <button
          type="button"
          className={style.rowActivator}
          aria-pressed={_isActive}
          onClick={() => onActivate?.(id)}
        >
          {slotCount === null || slotCount === undefined
            ? '—'
            : formatNumber({ value: slotCount, minPrecision: 0 })}
          {valuedCount !== undefined &&
            ` (${formatNumber({ value: valuedCount, minPrecision: 0 })})`}
        </button>
      );
    },
  },
];
